import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiPackage,
  FiClock,
  FiMapPin,
  FiRefreshCw,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Api from "../../axiosConfig";
import { toast } from "react-toastify";

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const [activeDeliveries, setActiveDeliveries] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [5, 10, 20, 40, 80];

  // Helper function to truncate address
  const truncateAddress = (address, maxLength = 30) => {
    if (!address) return "";
    return address.length > maxLength
      ? `${address.substring(0, maxLength)}...`
      : address;
  };
  const navigate = useNavigate();

  const fetchDeliveries = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);
      const response = await Api.get(
        `/v1/deliveries/my-deliveries?page=${page}&limit=${itemsPerPage}`
      );

      // Handle both array and object responses
      let deliveriesArray;
      if (Array.isArray(response.data)) {
        deliveriesArray = response.data;
      } else if (response.data && Array.isArray(response.data.deliveries)) {
        deliveriesArray = response.data.deliveries;
      } else {
        throw new Error(
          "Invalid response format - expected array of deliveries"
        );
      }

      // Sort by newest first and truncate addresses
      deliveriesArray = deliveriesArray
        .map((delivery) => ({
          ...delivery,
          pickupAddress: truncateAddress(delivery.pickupAddress),
          deliveryAddress: truncateAddress(delivery.deliveryAddress),
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setDeliveries(deliveriesArray);
      // Store deliveries in localStorage
      localStorage.setItem("deliveries", JSON.stringify(deliveriesArray));

      // If we got pagination info, store it and update state
      if (response.data.totalDeliveries) {
        const paginationData = {
          total: response.data.totalDeliveries,
          active: response.data.activeDeliveries,
          currentPage: response.data.currentPage || page,
          totalPages:
            response.data.totalPages ||
            Math.ceil(response.data.totalDeliveries / itemsPerPage),
        };

        localStorage.setItem("deliveriesStats", JSON.stringify(paginationData));

        setCurrentPage(paginationData.currentPage);
        setTotalPages(paginationData.totalPages);
        setTotalDeliveries(paginationData.total);
        setActiveDeliveries(paginationData.active);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch deliveries";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveries(currentPage);
  }, [currentPage, itemsPerPage]);

  const handleRefresh = () => {
    fetchDeliveries(currentPage);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "in_transit":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    } catch (error) {
      return dateString;
    }
  };

  const filteredAndSortedDeliveries = () => {
    let filtered = [...deliveries];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (delivery) =>
          delivery.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Always sort by createdAt descending (newest first)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Apply pagination - only return items for the current page
    const startIndex = 0; // Always show from the beginning of the filtered results
    const endIndex = itemsPerPage; // Only show the number of items per page
    
    return filtered.slice(startIndex, endIndex);
  };

  if (loading && deliveries.length === 0) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">My Deliveries</h1>
        <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="flex items-center space-x-2">
            <label
              htmlFor="statusFilter"
              className="text-sm font-medium text-gray-700 w-16"
            >
              Status:
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 min-w-[120px] rounded-md border-gray-300 border-[1px] border-solid shadow-sm focus:border-primary-dark focus:ring focus:ring-primary-dark focus:ring-opacity-50"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label
              htmlFor="sortBy"
              className="text-sm font-medium text-gray-700 w-16"
            >
              Sort by:
            </label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="flex-1 min-w-[120px] rounded-md border-gray-300 border-[1px] border-solid shadow-sm focus:border-primary-dark focus:ring focus:ring-primary-dark focus:ring-opacity-50"
            >
              <option value="date">Date Created</option>
              <option value="status">Status</option>
              <option value="id">ID</option>
            </select>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed self-end"
            title="Refresh deliveries"
          >
            <FiRefreshCw className={`${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pickup Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <FiRefreshCw className="w-6 h-6 animate-spin mx-auto text-primary-dark" />
                  </td>
                </tr>
              ) : filteredAndSortedDeliveries().length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    No deliveries found
                  </td>
                </tr>
              ) : (
                filteredAndSortedDeliveries().map((delivery) => (
                  <tr
                    key={delivery._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/delivery/${delivery._id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{delivery.orderId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {delivery.recipientName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {delivery.recipientPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">
                        {delivery.packageType}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {delivery.packageSize}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {delivery.pickupAddress}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {delivery.deliveryAddress}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                          delivery.status
                        )}`}
                      >
                        {delivery.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(delivery.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <Link
                        to={`/delivery/${delivery._id}`}
                        className="text-primary-dark hover:text-primary-light transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center border-t border-gray-200 border-solid sm:px-6">
            {/* Mobile view - improved layout */}
            <div className="w-full flex flex-col space-y-3 sm:hidden">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">
                    {filteredAndSortedDeliveries().length > 0
                      ? (currentPage - 1) * itemsPerPage + 1
                      : 0}
                  </span>
                  -
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalDeliveries)}
                  </span>{" "}
                  of <span className="font-medium">{totalDeliveries}</span>
                </p>
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="mobile-per-page"
                    className="text-sm text-gray-600"
                  >
                    Per page:
                  </label>
                  <select
                    id="mobile-per-page"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="block pl-3 pr-10 py-1 text-sm border-gray-300 border-[1px] border-solid focus:outline-none focus:ring-primary-dark focus:border-primary-dark rounded-md"
                  >
                    {itemsPerPageOptions.map((option) => (
                      <option key={`mobile-${option}`} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-between w-full">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex-1 mr-2 inline-flex justify-center items-center px-4 py-2 border text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex-1 ml-2 inline-flex justify-center items-center px-4 py-2 border text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                  <FiChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
              <div className="flex justify-center space-x-1">
                {[...Array(Math.min(5, totalPages)).keys()].map((idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }

                  if (pageNum <= totalPages) {
                    return (
                      <button
                        key={`mobile-page-${pageNum}`}
                        onClick={() => handlePageChange(pageNum)}
                        className={`inline-flex items-center justify-center w-8 h-8 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-primary-dark border-primary-dark text-white"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {filteredAndSortedDeliveries().length > 0
                      ? 1
                      : 0}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {filteredAndSortedDeliveries().length}
                  </span>{" "}
                  of <span className="font-medium">{totalDeliveries}</span>{" "}
                  results
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div>
                  <label
                    htmlFor="desktop-per-page"
                    className="text-sm text-gray-600 mr-2"
                  >
                    Items per page:
                  </label>
                  <select
                    id="desktop-per-page"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="mt-1 inline-block w-auto pl-3 pr-10 py-2 text-base border-gray-300 border-[1px] border-solid focus:outline-none focus:ring-primary-dark focus:border-primary-dark sm:text-sm rounded-md"
                  >
                    {itemsPerPageOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 border-solid bg-white text-sm font-medium${
                      currentPage === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <FiChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {/* Page Numbers */}
                  {[...Array(Math.min(5, totalPages)).keys()].map((idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    if (pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? "z-10 bg-primary-dark border-primary-dark text-white"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 border-solid bg-white text-sm font-medium${
                      currentPage === totalPages
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <FiChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deliveries;
